import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { CircularProgress, IconButton } from '@mui/material';
import { closeModal, toggleLoading } from '../../../state/ModalSlice/ModalSlice.ts';
import CloseIcon from "@mui/icons-material/Close";
import { useBackend } from '../../../hooks/backend/useBackend.ts';
import { toast } from 'react-toastify';
import { Timestamp } from 'firebase/firestore';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

export default function CancelPauseSubscriptionModal() {

    const Dispatch = useAppDispatch();
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)
    const open = useAppSelector((state: RootState) => state.ModalSlice.CancelPauseModalOpen)
    const ServiceProviderID = useAppSelector(selectEffectiveUID) 
    const LiveSubs = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data)
    const selectedLiveSubId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForCancelPauseModal.LiveSubscriptionId)
    const align2monthlyBoundary = useAppSelector((state: RootState) => state.ArbitrarySlice.ForCancelPauseModal.align2monthlyBoundary)

    const selectedLiveSub = LiveSubs?.filter((data) => data.id === selectedLiveSubId)[0]
    function handleClose() {
        Dispatch(closeModal("CancelPauseModalOpen"))
    }

    const { update } = useBackend(`/ServiceProviders/${ServiceProviderID}/LiveSubscriptions`)

    // Helper function to convert any date format to Date object
    const convertToDate = (dateValue: Date | string | { toDate: () => Date }) => {
        if (typeof dateValue === 'string') {
            return new Date(dateValue);
        } else if (dateValue instanceof Date) {
            return dateValue;
        } else if (typeof dateValue === 'object' && 'toDate' in dateValue) {
            return dateValue.toDate();
        }
        throw new Error('Invalid date format');
    };

    async function onSubmit() {
        if (!selectedLiveSub) return;
        
        Dispatch(toggleLoading(true))
        try {
            const pauseHistory = selectedLiveSub.pauseHistory || [];
            const currentPauseDaysLeft = selectedLiveSub.pauseDaysLeft || 0;
            const currentBillingEnd = selectedLiveSub.billingEnd ? 
                convertToDate(selectedLiveSub.billingEnd) : 
                new Date();

            if (pauseHistory.length > 0) {
                const lastPauseEntry = pauseHistory[pauseHistory.length - 1];
                const updatedPauseHistory = [...pauseHistory];

                // Calculate days between pauseTS and resumeTS
                if (lastPauseEntry.pauseTS && lastPauseEntry.resumeTS) {
                    const pauseDate = convertToDate(lastPauseEntry.pauseTS);
                    const resumeDate = convertToDate(lastPauseEntry.resumeTS);
                    const diffInMs = resumeDate.getTime() - pauseDate.getTime();
                    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;

                    // Adjust billing end date by removing the planned pause days
                    let newBillingEnd = new Date(currentBillingEnd);

                    // If align2monthlyBoundary is true, move to last day of previous month
                    if (align2monthlyBoundary && selectedLiveSub.billingCycle !== "FlexibleMonthly") {
                        newBillingEnd = new Date(newBillingEnd.getFullYear(), newBillingEnd.getMonth(), 0);
                    } else {
                        newBillingEnd.setDate(currentBillingEnd.getDate() - diffInDays);
                    }

                    // Remove the last pause entry and update pauseDaysLeft and billingEnd
                    updatedPauseHistory.pop();
                    await update(selectedLiveSub.id, {
                        pauseHistory: updatedPauseHistory,
                        pauseDaysLeft: currentPauseDaysLeft + diffInDays,
                        billingEnd: Timestamp.fromDate(newBillingEnd),
                        updatedAt: Timestamp.fromDate(new Date())
                    });
                }
            }

            toast.success("Pause request cancelled successfully");
        } catch (error) {
            console.error("Error cancelling pause request:", error);
            toast.error("Failed to cancel pause request");
        }

        Dispatch(toggleLoading(false))
        handleClose()
    }

    // Get pause period information for display
    const getPausePeriodText = () => {
        if (!selectedLiveSub) return "";
        
        const pauseHistory = selectedLiveSub.pauseHistory || [];
      
        if (pauseHistory.length > 0) {
            const lastEntry = pauseHistory[pauseHistory.length - 1];
            if (lastEntry.pauseTS && lastEntry.resumeTS) {
                const pauseDate = convertToDate(lastEntry.pauseTS);
                const resumeDate = convertToDate(lastEntry.resumeTS);
                return `There is an existing pause scheduled from ${pauseDate.toLocaleDateString('en-GB')} to ${resumeDate.toLocaleDateString('en-GB')}.`;
            }
        }
        return "";
    };

    return (
        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },

            }}
            maxWidth="md"
            open={open}
            disableEscapeKeyDown
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingX: "60px", paddingY: "40px" }} className={"bg-white"}>
                <div className={"header pb-[20px] min-w-[550px]"}>
                    <p className={"text-cf_heading font-[600] "}>Cancel Pause Request</p>
                </div>

                <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton></div>

                <div className={"pt-0"}>
                    <p>{getPausePeriodText()}</p>
                    <p>Do you want to cancel this pause request?</p>
                </div>

                <div className={"flex gap-2 pt-8"}>
                    <button onClick={handleClose} disabled={isLoading}
                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>
                    <button
                        onClick={onSubmit} disabled={isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>{isLoading &&
                            <CircularProgress sx={{ color: "white" }} size={20} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" : "Confirm"}</p>
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}